/**
 * FAQ 附件预览弹窗
 * 基于 FilePreviewModal 复制修改，接口简化为 FaqAttachment
 * 去掉了 storage_key / getSignedUrl 依赖，直接使用公开 URL
 * 支持：图片、PDF、视频、Office 文档
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Download, X, FileText,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

// PDF 预览
import { Document, Page, pdfjs } from 'react-pdf';
import type { FaqAttachment } from './types';

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

/** 根据文件名/MIME 判断预览类型 */
type PreviewType = 'image' | 'pdf' | 'video' | 'office' | 'unknown';

function getPreviewType(att: FaqAttachment): PreviewType {
  const ext = att.name.split('.').pop()?.toLowerCase() || '';
  const mime = att.type.toLowerCase();
  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/') || ['mp4', 'webm', 'mov'].includes(ext)) return 'video';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return 'office';
  return 'unknown';
}

function formatSize(bytes: number): string {
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

interface FaqAttachmentPreviewProps {
  attachment: FaqAttachment | null;
  isOpen: boolean;
  onClose: () => void;
}

const FaqAttachmentPreview: React.FC<FaqAttachmentPreviewProps> = ({
  attachment,
  isOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [officePreviewError, setOfficePreviewError] = useState(false);

  // 重置状态
  useEffect(() => {
    if (!isOpen) {
      setPageNumber(1);
      setNumPages(null);
      setIsLoading(true);
      setOfficePreviewError(false);
    }
  }, [isOpen]);

  if (!isOpen || !attachment) return null;

  const previewType = getPreviewType(attachment);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setIsLoading(false);
  };

  // 渲染预览内容
  const renderPreviewContent = () => {
    switch (previewType) {
      case 'image':
        return (
          <div className="flex items-center justify-center h-full p-4">
            <img
              src={attachment.url}
              alt={attachment.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
              onLoad={() => setIsLoading(false)}
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-auto flex items-center justify-center bg-gray-100">
              <Document
                file={attachment.url}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                }
                className="flex items-center justify-center"
              >
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                  height={Math.min(600, window.innerHeight - 200)}
                />
              </Document>
            </div>
            {numPages && (
              <div className="flex items-center justify-center py-3 space-x-4 bg-gray-50 border-t">
                <button
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                  className="p-2 text-gray-600 bg-white rounded-lg shadow-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 bg-white px-4 py-2 rounded-lg shadow-sm tabular-nums">
                  {pageNumber} / {numPages}
                </span>
                <button
                  onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                  disabled={pageNumber >= numPages}
                  className="p-2 text-gray-600 bg-white rounded-lg shadow-sm disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        );

      case 'video':
        return (
          <div className="flex items-center justify-center h-full bg-black p-4">
            <video
              src={attachment.url}
              controls
              className="max-w-full max-h-full rounded-lg"
              onLoadedData={() => setIsLoading(false)}
            />
          </div>
        );

      case 'office': {
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(attachment.url)}`;
        if (!officePreviewError) {
          return (
            <div className="w-full" style={{ minHeight: '70vh' }}>
              <iframe
                src={viewerUrl}
                className="w-full h-full border-none"
                style={{ minHeight: '70vh' }}
                title={`${attachment.name} 预览`}
                onLoad={() => setIsLoading(false)}
                onError={() => setOfficePreviewError(true)}
              />
            </div>
          );
        }
        return renderDownloadPrompt('Office 文档');
      }

      default:
        return renderDownloadPrompt('文件');
    }
  };

  const renderDownloadPrompt = (label: string) => {
    // 非预览类型时立即取消 loading
    if (isLoading) setTimeout(() => setIsLoading(false), 0);
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 text-center">
        <div className="w-20 h-20 rounded-2xl bg-slate-200 flex items-center justify-center mb-4">
          <FileText className="w-10 h-10 text-blue-500" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">
          {attachment.name}
        </h4>
        <p className="text-gray-500 mb-6">
          {label}暂不支持在线预览，请下载后查看
        </p>
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors flex items-center gap-2"
        >
          <Download className="w-5 h-5" />
          下载文件
        </a>
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-gray-200 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">
            {attachment.name}
          </h3>
          <div className="flex items-center gap-2">
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2.5 bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 rounded-xl transition-all"
              title="下载"
            >
              <Download className="w-5 h-5" />
            </a>
            <button
              onClick={onClose}
              className="p-2.5 bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-600 rounded-xl transition-all"
              title="关闭"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-auto bg-gray-100" style={{ minHeight: '60vh' }}>
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center min-h-[60vh]">
              <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-base font-medium text-gray-900 mt-2">{attachment.name}</p>
              <p className="text-sm text-gray-500 mt-1">正在加载中...</p>
            </div>
          )}
          {renderPreviewContent()}
        </div>

        {/* 底部信息栏 */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>大小: {formatSize(attachment.size)}</span>
            <span>类型: {attachment.type}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FaqAttachmentPreview;
